import {FieldSeasonShortDto, LocalizationString} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {ServerTranslateService} from '@services/server-translate.service';
import {CROP_COLOR_MAP} from '@app/utils/_crop-colors';

export const FIELD_LIST_SEASON_BADGE_PLAN_COLOR = '#6B7280';

export interface FieldSeasonRowView {
  id: string;
  labelKey: string;
  tagColor: string;
  cropName: string;
  cropColorHex: string | null;
  isPlanned: boolean;
  plantingDate: string | null;
  plantingDatePlan: string | null;
  harvestingDate: string | null;
  harvestingDatePlan: string | null;
  fieldArea: number | null;
}

export function sortSeasonsForFieldList(seasons: FieldSeasonShortDto[]): FieldSeasonShortDto[] {
  const planSeasons = seasons.filter(s => s.isPlanned === true);
  const factSeasons = seasons.filter(s => s.isFact === true && !s.isPlanned);

  const result: FieldSeasonShortDto[] = [];
  const usedFactIds = new Set<string>();

  for (const plan of planSeasons) {
    result.push(plan);
    const linkedFacts = factSeasons.filter(f => f.planSeasonId === plan.id);
    for (const fact of linkedFacts) {
      result.push(fact);
      if (fact.id) {
        usedFactIds.add(fact.id);
      }
    }
  }

  const orphanFacts = factSeasons.filter(f => f.id && !usedFactIds.has(f.id));
  result.push(...orphanFacts);

  return result;
}

export function buildFieldSeasonRowViews(
  seasons: FieldSeasonShortDto[],
  serverTranslate: ServerTranslateService,
): FieldSeasonRowView[] {
  return sortSeasonsForFieldList(seasons).map(season => toFieldSeasonRowView(season, serverTranslate));
}

function toFieldSeasonRowView(
  season: FieldSeasonShortDto,
  serverTranslate: ServerTranslateService,
): FieldSeasonRowView {
  const cropNamesKey = resolveSeasonCropNamesKey(season);
  const cropName = serverTranslate.translate(season, cropNamesKey) as string | null;

  return {
    id: season.id ?? '',
    labelKey: resolveSeasonLabelKey(season),
    tagColor: resolveSeasonTagColor(season),
    cropName: cropName?.trim() ? cropName : '-',
    cropColorHex: resolveSeasonCropColorHex(season, cropNamesKey, serverTranslate),
    isPlanned: season.isPlanned === true,
    plantingDate: season.plantingDate ?? null,
    plantingDatePlan: season.plantingDatePlan ?? null,
    harvestingDate: season.harvestingDate ?? null,
    harvestingDatePlan: season.harvestingDatePlan ?? null,
    fieldArea: season.fieldArea ?? null,
  };
}

function resolveSeasonLabelKey(season: FieldSeasonShortDto): string {
  if (season.isPlanned === true) return 'PLAN_FACT_PLAN';
  if (season.isFact === true) return 'PLAN_FACT_FACT';
  return 'PLAN_FACT_PLAN';
}

function resolveSeasonTagColor(season: FieldSeasonShortDto): string {
  if (season.isPlanned === true) return FIELD_LIST_SEASON_BADGE_PLAN_COLOR;
  if (season.isFact === true) return 'success';
  return FIELD_LIST_SEASON_BADGE_PLAN_COLOR;
}

function resolveSeasonCropNamesKey(season: FieldSeasonShortDto): 'cropNames' | 'plannedCropNames' {
  if (season.isPlanned !== true) return 'cropNames';
  return hasLocalizationValues(season.plannedCropNames) ? 'plannedCropNames' : 'cropNames';
}

function resolveSeasonCropColorHex(
  season: FieldSeasonShortDto,
  cropNamesKey: 'cropNames' | 'plannedCropNames',
  serverTranslate: ServerTranslateService,
): string | null {
  const nameEn = serverTranslate.translateForLanguage(season, cropNamesKey, 'en-US');
  if (!nameEn) return null;
  return CROP_COLOR_MAP[nameEn] ?? null;
}

function hasLocalizationValues(names?: LocalizationString | null): boolean {
  if (!names) return false;
  return Object.values(names).some(value => value != null && value !== '');
}

